import { useMemo } from 'react';
import { HoaDonDieuChinhGiaHangBanSearchProvider, useHoaDonDieuChinhGiaHangBanSearchContext } from './context';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ConfirmationDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { getDataTableColumns, getDetailDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { useCRUD, useForms, usePatch } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

const PREVIEW_ACTION_ORDER = ['export', 'pin'] as const;

function HoaDonDieuChinhGiaHangBanContent() {
  const { handleSearchChange, searchState } = useHoaDonDieuChinhGiaHangBanSearchContext();
  const {
    data,
    isLoading,
    fetchDetail,
    detail,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    totalItems,
    currentPage,
    handlePageChange
  } = useCRUD({
    endpoint: QUERY_KEYS.HOA_DON_DIEU_CHINH_GIA_HANG_BAN
  });
  const { patchItem } = usePatch({ endpoint: `${QUERY_KEYS.HOA_DON_BAN_HANG}/chi-tiet-hoa-don` });

  const {
    showForm,
    showDelete,
    showSearch,
    formMode,
    isCopyMode,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleSearch,
    handleCloseSearch
  } = useForms(fetchDetail);

  const handleSearchSubmit = (filters: BanHangPayload) => {
    return filters;
  };

  const hasFilterParams = searchState.filterParams && Object.keys(searchState.filterParams).length > 0;
  const displayData = hasFilterParams ? searchState.searchResults : data;
  const mainColumns = useMemo(() => getDataTableColumns(handleViewClick), [handleViewClick]);
  const previewActions = useMemo(
    () => ({
      export: () => {},
      pin: () => {}
    }),
    []
  );

  const tables = [
    {
      name: 'Tất cả',
      rows: displayData,
      columns: mainColumns
    },
    {
      name: 'Lập chứng từ',
      rows: displayData?.filter(row => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: displayData?.filter(row => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Xuất hóa đơn',
      rows: displayData?.filter(row => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    },
    {
      name: 'Khác',
      rows: displayData?.filter(row => row.status === '99'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-black' />
    }
  ];

  return (
    <div className='flex h-screen w-screen flex-col'>
      {showSearch && <SearchDialog open={showSearch} onClose={handleCloseSearch} onSearch={handleSearchSubmit} />}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
          addItem={addItem}
          updateItem={updateItem}
          patchItem={patchItem}
          handleCloseForm={handleCloseForm}
          clearSelection={clearSelection}
          refreshData={refreshData}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshData();
              if (selectedObj) await fetchDetail(selectedObj.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          <ResizablePanelGroup direction='vertical' className='min-h-0 flex-1 overflow-hidden'>
            <ResizablePanel defaultSize={50} minSize={20}>
              <div data-perf-id='hddc-gia-hang-ban-master-table' className='h-full w-full overflow-hidden'>
                <ErpDataTableV2
                  tables={tables}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowIndex || undefined}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onSearchChange={handleSearchChange}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={20}>
              <div data-perf-id='hddc-gia-hang-ban-bottom-detail-table' className='h-full overflow-hidden'>
                <ErpInputTableV2
                  rows={selectedObj && !isLoading ? detail?.chi_tiet || [] : []}
                  columns={getDetailDataTableColumns}
                  showIndexColumn
                  actions={previewActions}
                  actionOrder={PREVIEW_ACTION_ORDER as BanHangRecord}
                  actionsDisabled={formMode === 'view' || !selectedObj}
                  height={'100%'}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </>
      )}
      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={() => {
            deleteItem(selectedObj!.uuid);
            handleCloseDelete();
            clearSelection();
            handleCloseForm();
          }}
          message='Bạn có chắc chắn muốn xóa chứng từ này?'
        />
      )}
    </div>
  );
}

export default function HoaDonDieuChinhGiaHangBan() {
  return (
    <HoaDonDieuChinhGiaHangBanSearchProvider>
      <HoaDonDieuChinhGiaHangBanContent />
    </HoaDonDieuChinhGiaHangBanSearchProvider>
  );
}
